import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import moment from 'moment-timezone';
import { sqDurationStore, sqTrendConditionStore, sqTrendSeriesStore } from '@/core/core.stores';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore, type ToolItem } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  DEFAULT_MAX_ITERATIONS,
  DEFAULT_NUM_COMPONENTS,
  DEFAULT_SCALE,
  DEFAULT_TOLERANCE,
  INPUT_NAMES,
} from '@/tools/partialLeastSquaresV2/partialLeastSquares.constants';
import type { BaseToolPersistedState, ModelRehydratePayload, ToolParameter } from '@/toolSelection/baseTool.store';
import type { Capsule } from '@/utilities/datetime.constants';

interface PartialLeastSquaresState extends BaseToolPersistedState {
  windowStart: number;
  windowEnd: number;
  window: any;
  modelPropertiesExpanded: boolean;
  numComponents: number;
  scale: boolean;
  maxIterations: number;
  tolerance: number;
  tableApplicationId?: string;
  instanceId?: string;
}

export type PartialLeastSquaresDehydratedState = Omit<PartialLeastSquaresState, 'window'>;

export class PartialLeastSquaresStoreV2 extends BaseToolStore {
  static readonly storeName = 'sqPartialLeastSquaresStoreV2';
  type = TREND_TOOLS.PARTIAL_LEAST_SQUARES_V_2;
  parameterDefinitions = {
    condition: {
      predicate: ['name', 'condition'],
    },
    restrictToCondition: {
      predicate: ['name', 'restrictToCondition'],
    },
    targetSignal: { predicate: ['name', 'target'] },
    inputSignals: {
      predicate: (parameter: ToolParameter) => parameter?.name?.startsWith(INPUT_NAMES.signalInput),
      multiple: true,
    },
  };

  initialize() {
    const initialState: PartialLeastSquaresState = {
      ...BASE_TOOL_COMMON_PROPS,
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
      window: this.monkey(['windowStart'], ['windowEnd'], (startTime, endTime) => ({ startTime, endTime })),
      modelPropertiesExpanded: false,
      numComponents: DEFAULT_NUM_COMPONENTS,
      scale: DEFAULT_SCALE,
      maxIterations: DEFAULT_MAX_ITERATIONS,
      tolerance: DEFAULT_TOLERANCE,
      tableApplicationId: undefined,
      instanceId: undefined,
    };
    this.state = this.immutable(initialState);
  }

  get inputSignals(): ToolItem[] {
    return this.state.get('inputSignals');
  }

  get targetSignal(): ToolItem | undefined {
    return this.state.get('targetSignal');
  }

  get condition(): ToolItem | undefined {
    return this.state.get('condition');
  }

  get restrictToCondition(): ToolItem | undefined {
    return this.state.get('restrictToCondition');
  }

  get window() {
    return this.state.get('window');
  }

  get modelPropertiesExpanded(): boolean {
    return this.state.get('modelPropertiesExpanded');
  }

  get numComponents(): number {
    return this.state.get('numComponents');
  }

  get scale(): boolean {
    return this.state.get('scale');
  }

  get maxIterations(): number {
    return this.state.get('maxIterations');
  }

  get tolerance(): number {
    return this.state.get('tolerance');
  }

  get tableApplicationId() {
    return this.state.get('tableApplicationId');
  }

  get instanceId() {
    return this.state.get('instanceId');
  }

  dehydrate(): PartialLeastSquaresDehydratedState {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: Partial<PartialLeastSquaresDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  setTrainingWindow(payload: { window: Capsule }): void {
    this.state.set('windowStart', _.get(payload.window, 'startTime'));
    this.state.set('windowEnd', _.get(payload.window, 'endTime'));
  }

  setModelPropertiesExpanded(payload: { modelPropertiesExpanded: boolean }): void {
    this.state.set('modelPropertiesExpanded', payload.modelPropertiesExpanded);
  }

  setScale(payload: { scale: boolean }): void {
    this.state.set('scale', payload.scale);
  }

  setMaxIterations(payload: { maxIterations: number }): void {
    this.state.set('maxIterations', payload.maxIterations);
  }

  setNumComponents(payload: { numComponents: number }): void {
    this.state.set('numComponents', payload.numComponents);
  }

  setTolerance(payload: { tolerance: number }): void {
    this.state.set('tolerance', payload.tolerance);
  }

  override modifyConfigParams<TConfig extends Record<string, unknown>>(config: TConfig): TConfig {
    return _.omit(config, ['modelPropertiesExpanded']);
  }

  modelToolRehydrateForEdit = (payload: ModelRehydratePayload): void => {
    if (payload.type !== this.type) {
      return;
    }
    this.initialize();
    this.state.set('id', payload.id);
    this.state.set('instanceId', payload.instanceId);
    this.state.set('tableApplicationId', payload.instanceData?.tableApplicationId);
    (payload.modelData?.inputFeatures ?? []).forEach((feature: any): void => {
      const inputName: string = feature.name;
      switch (inputName) {
        case SeeqNames.Models.InstanceNameInputFeatureName:
          // TODO: CRAB-53864: Handle duplicate names more gracefully
          this.state.set('name', payload.instanceId ? feature.inputText : `${feature.inputText} (Copy)`);
          break;
        case INPUT_NAMES.nComponentsInput:
          this.state.set('numComponents', feature.inputNumeric ?? DEFAULT_NUM_COMPONENTS);
          break;
        case INPUT_NAMES.scaleInput:
          this.state.set('scale', feature.inputBoolean ?? DEFAULT_SCALE);
          break;
        case INPUT_NAMES.maxIterationsInput:
          this.state.set('maxIterations', feature.inputNumeric ?? DEFAULT_MAX_ITERATIONS);
          break;
        case INPUT_NAMES.toleranceInput:
          this.state.set('tolerance', feature.inputNumeric ?? DEFAULT_TOLERANCE);
          break;
        case INPUT_NAMES.startTimeInput: {
          const startTimeInput = JSON.parse(feature.inputText);
          this.state.set('windowStart', moment(startTimeInput).valueOf());
          break;
        }
        case INPUT_NAMES.endTimeInput: {
          const endTimeInput = JSON.parse(feature.inputText);
          this.state.set('windowEnd', moment(endTimeInput).valueOf());
          break;
        }
        case INPUT_NAMES.trainWithinConditionInput:
          if (feature.inputItem) {
            const item = sqTrendConditionStore.findItem(feature.inputItem) || { id: feature.inputItem };
            this.state.set('condition', _.pick(item, ['id', 'name']));
          }
          break;

        case INPUT_NAMES.restrictOutputToConditionInput: {
          if (feature.inputItem) {
            const item = sqTrendConditionStore.findItem(feature.inputItem) || { id: feature.inputItem };
            this.state.set('restrictToCondition', _.pick(item, ['id', 'name']));
          }
          break;
        }
        case INPUT_NAMES.targetSignalInput: {
          const item = sqTrendSeriesStore.findItem(feature.inputItem) || { id: feature.inputItem };
          this.state.set('targetSignal', _.pick(item, ['id', 'name']));
          break;
        }
        default:
          if (inputName.startsWith(INPUT_NAMES.signalInput) && feature.inputItem) {
            const existingSignals = this.state.get('inputSignals') || [];
            const item = sqTrendSeriesStore.findItem(feature.inputItem) || { id: feature.inputItem };
            this.state.set('inputSignals', [...existingSignals, _.pick(item, ['id', 'name'])]);
          }
          break;
      }
    });
  };

  protected readonly handlers = {
    ...this.baseHandlers,
    PLS_SET_TRAINING_WINDOW: this.setTrainingWindow,
    PLS_SET_MODEL_PROPERTIES_EXPANDED: this.setModelPropertiesExpanded,
    PLS_SET_NUM_COMPONENTS: this.setNumComponents,
    PLS_SET_SCALE: this.setScale,
    PLS_SET_MAX_ITERATIONS: this.setMaxIterations,
    PLS_SET_TOLERANCE: this.setTolerance,
    MODEL_TOOL_REHYDRATE_FOR_EDIT: this.modelToolRehydrateForEdit,
  };
}
